import type { ReactNode } from 'react';
import { cn } from '../ui/cn';
import { CodeChip } from '../ui/CodeChip';
import { entityHue } from '../ui/EntityAvatar';

/**
 * Small gold "master" capsule so the master warehouse is identifiable in EVERY warehouse picker across the app.
 * Matches the gold/harvest accent used for the master warehouse elsewhere (e.g. the Master Stock banner tag).
 */
export function MasterTag({ className = '' }: { className?: string }) {
  return (
    <span className={`ml-1.5 shrink-0 whitespace-nowrap rounded-full border border-harvest-500/45 bg-harvest-400/25 px-1.5 py-px type-overline text-harvest-700 dark:border-harvest-400/40 dark:bg-harvest-400/20 dark:text-harvest-300 ${className}`}>
      master
    </span>
  );
}

type WhLike = { code?: string | null; name?: string | null; isMaster?: boolean | number | null };

/** "CODE · Name" plain text — the searchable / `title` form of a warehouse, for a tooltip, a filter chip
 *  or an option's `title` (which is what a ui/Select searches when the visible label is a node). */
export const warehouseText = (w: WhLike): string => [w.code, w.name].filter(Boolean).join(' · ') || '—';

/**
 * The warehouse CODE as a chip, in that warehouse's own colour.
 *
 * A warehouse has two names for one thing — the code people say out loud ("send it from MUM") and the
 * long name that identifies the site. Joined by a dot they read as one run-on string, and the code, the
 * half being scanned for, is the half that disappears. So the code becomes a chip: its own shape, its
 * own tint, always at the left edge, i.e. a column the eye can run down. The hue is the identity hue
 * from EntityAvatar, so WH-MUM is the same colour here, in a picker and beside an avatar anywhere else.
 */
export function WarehouseCode({ code, className }: { code: string; className?: string }) {
  return <CodeChip hue={entityHue(code)} className={cn('shrink-0 min-w-[5.5rem] text-center', className)}>{code}</CodeChip>;
}

/**
 * Label node for a warehouse option: the code chip, the site name, and a gold capsule when it is the
 * master warehouse. Use as the option `label`; pair it with `warehouseText(w)` as the option `title`
 * so a searchable Select still matches on code AND name — `warehouseOption(w)` does both for you.
 */
export function warehouseLabel(w: WhLike): ReactNode {
  const code = String(w.code ?? '').trim();
  const name = String(w.name ?? '').trim();
  if (!code && !name) return '—';
  /*
   * `flex`, not `inline-flex`, and capped at the width it is given.
   *
   * A ui/Select's collapsed trigger wraps the selected label in a `truncate` span, and `truncate` is
   * `overflow: hidden` plus an ellipsis that only applies to INLINE content — so an inline-flex label
   * sized to its own content simply overflowed and was hard-clipped by the trigger. On the master
   * warehouse that cut the gold capsule in half ("Pune Warehouse MAS"), which reads as a rendering
   * fault rather than as a name too long for its box.
   *
   * Block-level and `max-w-full`, the label is constrained by the trigger, so the pressure lands on
   * the one part that can absorb it: the NAME ellipsises. The code chip and the master capsule both
   * stay whole, which is the right order of sacrifice — they are what tells two warehouses apart,
   * and the tail of a site name is not.
   */
  return (
    <span className="flex min-w-0 max-w-full items-center">
      <span className="flex min-w-0 items-center gap-1.5">
        {code ? <WarehouseCode code={code} /> : null}
        {name ? <span className="min-w-0 truncate">{name}</span> : null}
      </span>
      {w.isMaster ? <MasterTag /> : null}
    </span>
  );
}

/** Everything one warehouse option needs: the label node, plus the plain text both `searchLabel` and
 *  `title` want (a ui/Select falls back to `title` when the label is a node, so search keeps working).
 *  Spread it: `{ value: String(w.id), ...warehouseOption(w) }`. */
export function warehouseOption(w: WhLike) {
  return { label: warehouseLabel(w), searchLabel: warehouseText(w), title: warehouseText(w) };
}
